import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, createSignal, For, Show } from "solid-js";
import type { PageMapLens } from "../../enums/map";
import { EstateClusterBand } from "./EstateClusterBand";
import {
  EstateClusterEdgeLayer,
  EstatePageEdgeLayer,
} from "./EstateMapEdgeLayers";
import { EstateMapNode } from "./EstateMapNode";
import { EstateMapOverlay } from "./EstateMapOverlay";
import { EstateMapTooltip } from "./EstateMapTooltip";
import { layoutEstate } from "./estateLayout";
import {
  buildClusterEdges,
  buildEdges,
  connectedPageIds,
  estateEdgeKey,
  includeFocusedEdges,
  selectVisibleEdges,
} from "./estateRelationships";
import { fitTransform, lensLabel } from "./estateViewport";
import { createEstateViewport } from "./useEstateViewport";

export function InteractiveEstateMap(props: {
  data: OverviewResponse;
  lens: PageMapLens;
  selectedPageId: string | null;
  onSelect: (pageId: string) => void;
  onClearSelection?: () => void;
  compact?: boolean;
  activeCluster?: string | null;
  routeFocusActive?: boolean;
  focusedPageIds?: string[];
  focusedLinks?: OverviewResponse["links"];
}) {
  let mapElement!: SVGSVGElement;
  const [hoveredPageId, setHoveredPageId] = createSignal<string | null>(null);
  const compact = () => Boolean(props.compact);
  const layout = createMemo(() =>
    layoutEstate(props.data, props.lens, compact()),
  );
  const nodes = createMemo(() => layout().nodes);
  const nodeById = createMemo(
    () => new Map(nodes().map((node) => [node.page.id, node])),
  );
  const selectedNode = createMemo(() =>
    props.selectedPageId
      ? (nodeById().get(props.selectedPageId) ?? null)
      : null,
  );
  const hoveredNode = createMemo(() => {
    const pageId = hoveredPageId();
    return pageId ? (nodeById().get(pageId) ?? null) : null;
  });
  const tooltipNode = createMemo(() => {
    const node = hoveredNode();
    if (compact() && node?.page.id === props.selectedPageId) {
      return null;
    }
    return node;
  });
  const focusedCluster = createMemo(
    () =>
      hoveredNode()?.cluster ??
      selectedNode()?.cluster ??
      props.activeCluster ??
      null,
  );
  const allEdges = createMemo(() => buildEdges(props.data, nodes()));
  const routeFocus = createMemo(() => ({
    active: Boolean(props.routeFocusActive && props.focusedPageIds?.length),
    pageIds: new Set(props.focusedPageIds ?? []),
    edgeKeys: new Set(
      (props.focusedLinks ?? []).map((link) =>
        estateEdgeKey(link.fromPageId, link.toPageId),
      ),
    ),
  }));
  const contextPageId = createMemo(
    () => hoveredPageId() ?? props.selectedPageId ?? null,
  );
  const visibleEdges = createMemo(() => {
    if (compact() && !contextPageId()) {
      return [];
    }
    const selected = selectVisibleEdges(
      allEdges(),
      contextPageId(),
      compact() ? 6 : contextPageId() ? 8 : 14,
      props.lens,
    );
    return routeFocus().active
      ? includeFocusedEdges(selected, allEdges(), routeFocus().edgeKeys)
      : selected;
  });
  const connectedIds = createMemo(() =>
    connectedPageIds(visibleEdges(), contextPageId()),
  );
  const clusterEdges = createMemo(() =>
    buildClusterEdges(props.data, layout().bands),
  );
  const fitView = createMemo(() => fitTransform(layout(), compact(), null));
  const hasWeak = createMemo(() =>
    nodes().some((node) => node.state === "poor"),
  );
  const hasImportant = createMemo(() => nodes().some((node) => node.important));
  const hasBroken = createMemo(
    () =>
      visibleEdges().some((edge) => edge.link.agentBroken) ||
      clusterEdges().some((edge) => edge.brokenCount > 0),
  );
  const {
    effectiveView,
    isPanning,
    worldTransform,
    zoomPercent,
    zoomBy,
    fitEstate,
    svgHandlers,
  } = createEstateViewport({
    compact,
    layout,
    fitView,
    nodeById,
    selectedPageId: () => props.selectedPageId,
    setHoveredPageId,
  });

  return (
    <div
      class={`signal-estate-map-shell ${compact() ? "is-compact" : "is-navigable"} ${
        isPanning() ? "is-panning" : ""
      }`}
      style={{
        "aspect-ratio": `${layout().viewport.width} / ${
          layout().viewport.height + (compact() ? 0 : 6.5)
        }`,
      }}
    >
      <svg
        ref={mapElement}
        class={`signal-estate-map ${compact() ? "is-compact" : "is-navigable"}`}
        viewBox={`0 0 ${layout().viewport.width} ${layout().viewport.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${
          compact()
            ? "Site structure at a glance"
            : "Interactive page navigator"
        } using the ${lensLabel(props.lens)} view`}
        onKeyDown={(event) => {
          if (event.key === "Escape" && props.selectedPageId) {
            event.preventDefault();
            props.onClearSelection?.();
          }
        }}
        onPointerLeave={() => setHoveredPageId(null)}
        onWheel={svgHandlers.onWheel}
        onPointerDown={svgHandlers.onPointerDown}
        onPointerMove={svgHandlers.onPointerMove}
        onPointerUp={svgHandlers.onPointerUp}
        onPointerCancel={svgHandlers.onPointerCancel}
      >
        <g class="signal-estate-map-world" transform={worldTransform()}>
          <EstateClusterEdgeLayer
            clusterEdges={clusterEdges()}
            focusedCluster={focusedCluster()}
            routeFocusActive={routeFocus().active}
          />

          <g class="signal-cluster-bands">
            <For each={layout().bands}>
              {(band) => (
                <EstateClusterBand
                  band={band}
                  compact={compact()}
                  active={focusedCluster() === band.key}
                />
              )}
            </For>
          </g>

          <EstatePageEdgeLayer
            visibleEdges={visibleEdges()}
            contextPageId={contextPageId()}
            routeFocus={routeFocus()}
          />

          <g class="signal-estate-nodes">
            <For each={nodes()}>
              {(node) => (
                <EstateMapNode
                  node={node}
                  site={props.data.site}
                  lens={props.lens}
                  compact={props.compact}
                  selectedPageId={props.selectedPageId}
                  activeCluster={focusedCluster()}
                  contextPageId={contextPageId()}
                  connectedIds={connectedIds()}
                  routeFocusActive={routeFocus().active}
                  focusedPageIds={routeFocus().pageIds}
                  hoveredPageId={hoveredPageId()}
                  onHover={setHoveredPageId}
                  onSelect={props.onSelect}
                />
              )}
            </For>
          </g>
        </g>
      </svg>

      <Show when={tooltipNode()} keyed>
        {(node) => (
          <EstateMapTooltip
            node={node}
            mapElement={mapElement}
            view={effectiveView()}
            viewport={layout().viewport}
            site={props.data.site}
            lens={props.lens}
          />
        )}
      </Show>

      <EstateMapOverlay
        compact={compact()}
        zoomPercent={zoomPercent()}
        pageCount={props.data.pages.length}
        representedPageCount={nodes().length}
        clusterCount={layout().bands.length}
        hasWeak={hasWeak()}
        hasImportant={hasImportant()}
        hasBroken={hasBroken()}
        hasSelection={Boolean(props.selectedPageId)}
        onZoomOut={() => zoomBy(0.82)}
        onZoomIn={() => zoomBy(1.22)}
        onFit={fitEstate}
      />
    </div>
  );
}
